"use client";

import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@workspace/ui/components/dropdown-menu";
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuTrigger,
} from "@workspace/ui/components/navigation-menu";
import { LayoutDashboard } from "lucide-react";
import Link from "next/link";
import { NavItemProps } from "../types";
import { isButtonNavItem, isLinkNavItem, isSubMenuNavItem } from "../utils";

export function DropdownNavItem({ item }: NavItemProps) {
  if (!item.visible) return null;

  if (isLinkNavItem(item)) {
    return (
      <DropdownMenuItem asChild>
        <Link href={item.href}>
          <item.icon className="h-4 w-4" />
          {item.name}
        </Link>
      </DropdownMenuItem>
    );
  }

  if (isButtonNavItem(item)) {
    return (
      <DropdownMenuItem onClick={item.onClick}>
        <item.icon className="h-4 w-4" />
        {item.name}
      </DropdownMenuItem>
    );
  }

  if (isSubMenuNavItem(item)) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <item.icon className="h-4 w-4" />
          {item.name}
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent className="bg-background/50 min-w-40 backdrop-blur-xl">
            {item.children.map((child) => (
              <DropdownNavItem key={child.name} item={child} />
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
    );
  }

  return null;
}

export function NavigationNavItem({ item }: NavItemProps) {
  if (!item.visible) return null;

  const isMyLocal = item.name === "My Local";
  if (isMyLocal && isLinkNavItem(item)) {
    return (
      <NavigationMenuItem>
        <NavigationMenuLink asChild>
          <Button
            variant="ghost"
            asChild
            tabIndex={0}
            className="hover:bg-brand-100 text-brand-700"
          >
            <Link href={item.href} className="flex items-center gap-1">
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden md:inline">My Local</span>
            </Link>
          </Button>
        </NavigationMenuLink>
      </NavigationMenuItem>
    );
  }

  if (isLinkNavItem(item)) {
    return (
      <NavigationMenuItem>
        <NavigationMenuLink asChild>
          <Button
            variant="ghost"
            asChild
            tabIndex={0}
            className="hover:bg-brand-100 text-brand-700"
          >
            <Link href={item.href}>{item.name}</Link>
          </Button>
        </NavigationMenuLink>
      </NavigationMenuItem>
    );
  }

  if (isButtonNavItem(item)) {
    return (
      <NavigationMenuItem>
        <NavigationMenuLink asChild>
          <Button onClick={item.onClick}>{item.name}</Button>
        </NavigationMenuLink>
      </NavigationMenuItem>
    );
  }

  if (isSubMenuNavItem(item)) {
    return (
      <NavigationMenuItem>
        <NavigationMenuTrigger>{item.name}</NavigationMenuTrigger>
        <NavigationMenuContent>
          {item.children.map((child) => (
            <NavigationMenuItem key={child.name}>
              <NavigationMenuLink asChild>{child.name}</NavigationMenuLink>
            </NavigationMenuItem>
          ))}
        </NavigationMenuContent>
      </NavigationMenuItem>
    );
  }

  return null;
}
